import {
  Box,
  Button,
  Icon,
  Modal,
  Table,
  TableData
} from '@platform/ui-library';
import { FC, useMemo } from 'react';
import { languageSpeakDatesAsOptions } from '@app/const';
import {
  useAsync,
  useDesign,
  useServices
} from '@app/hooks';
import {
  AudioType,
  DesignLanguage
} from '@app/types';
import { LanguageForm, languageTypeLabels } from './language-form';
import styles from '@app/styles/components/modal.module.scss';

type LanguageListProps = {
  isOpen?: boolean;
  onChange: () => void;
  onComplete: () => void;
};

const columnStyles: string[] = [
  '20%',
  '20%',
  '30%',
  '20%',
  '10%'
];

export const LanguageList: FC<LanguageListProps> = ({
  onChange,
  onComplete,
  ...modalProps
}) => {
  const serialized = useDesign();
  const { languages } = serialized;
  const { workflowService } = useServices();

  const [languageOptions] = useAsync(() => workflowService.getLanguages(), []);
  const [allVoices] = useAsync(() => workflowService.getVoices(), []);

  const handleSave = (language: DesignLanguage, action: 'update' | 'delete', index = -1) => {
    let languages = [
      ...serialized.languages
    ];
    if (action === 'delete') {
      languages = languages.filter((_, i) => i !== index);
    } else {
      languages[(index === -1) ? languages.length : index] = language;
    }

    window.apm.setBasic('languages', languages);
    serialized.languagesMeta = serialized.languagesMeta ?? {} as any;
    serialized.languagesMeta!.lastLocalChange = window.apm.timestamp;

    window.apm.setSerializedChanged();
    window.apm.postEventToAllViews({ target: 'request-refresh' });

    onChange();
  };

  const tableData: TableData = useMemo(() => {
    return languages.map((language, index) => {
      const { language: languageCode, speakDatesAs, ttsFallBackVoice, voice } = language;
      const languageObject = languageOptions?.find(({ value }) => value === languageCode);
      const languageText = languageObject?.label ?? languageCode;
      const [languageName, languageDescription] = splitParentheses(languageText);
      const ttpObject = allVoices?.find(({ value }) => value === voice);
      const ttpName = ttpObject?.label ?? voice;
      const ttpVendor = (ttpName && ttpObject?.vendor) ?? null;
      const ttsObject = allVoices?.find(({ value }) => value === ttsFallBackVoice);
      const ttsName = ttsObject?.label ?? ttsFallBackVoice;
      const ttsDescription = (ttsName && ttsObject?.description) ?? null;
      const ttsVendor = (ttsName && ttsObject?.vendor) ?? null;
      const speakDatesAsLabel = languageSpeakDatesAsOptions.find(({ value }) => {
        return value === (speakDatesAs ?? null);
      })?.label ?? 'No Format';

      return {
        [languageTypeLabels.language]: {
          content: (
            <>
              <strong>{languageName}</strong>
              {!!languageDescription && <small> ({languageDescription})</small>}
            </>
          ),
          raw: languageName
        },
        [languageTypeLabels[AudioType.ttp]]: {
          content: (
            <>
              {ttpName} {ttpVendor && <small> ({ttpVendor})</small>}
            </>
          ),
          raw: ttpName ?? ''
        },
        [languageTypeLabels[AudioType.tts]]: {
          content: (
            <>
              {ttsName} {ttsVendor && <small> ({ttsVendor})</small>}
              {ttsDescription && <p className={styles.description}>{ttsDescription}</p>}
            </>
          ),
          raw: ttsName ?? ''
        },
        [languageTypeLabels.ttsDateFormat]: {
          content: (
            <>{speakDatesAsLabel}</>
          ),
          raw: speakDatesAsLabel
        },
        _action: {
          content: (
            <LanguageForm
              deletable={index !== 0}
              labels={{
                [AudioType.tts]: ttsName ?? '',
                [AudioType.ttp]: ttpName ?? '',
                'language': languageText ?? ''
              }}
              language={language}
              onSave={(updatedLanguage: DesignLanguage, action) => handleSave(updatedLanguage, action, index)}
            >
              <Icon icon="edit" />
            </LanguageForm>
          ),
          raw: ''
        }
      };
    });
  }, [languages, languageOptions, allVoices]);

  return (
    <Modal {...modalProps}>
      <Box className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h3>{window.l.popup.languages}</h3>
            <LanguageForm
              deletable={false}
              onSave={(updatedLanguage: DesignLanguage) => handleSave(updatedLanguage, 'update')}
            >
              <Icon icon="plus" />
              <span>Add Language</span>
            </LanguageForm>
          </div>
          <div className={styles.tableWrapper}>
            <Table
              columnStyles={columnStyles}
              data={tableData}
              key={JSON.stringify(languages)}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            onClick={() => {
              onComplete();
            }}
            theme="outline"
            type='button'
          >
            Close
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

const splitParentheses = (text: string): [string, string] => {
  const match = text.match(/(.*?)\s*\((.*?)\)/);
  if (!match) {
    return [text, ''];
  }
  return [match[1].trim(), match[2].trim()];
};
